import type {
  ImpactCategory,
  MatrixEntry,
  RiskScoringLevel,
} from '@risksmart-app/atomic-ui';

/**
 * Represents the settings data that can be compared for changes.
 * This is the subset of state that represents persisted configuration.
 */
export interface ScoringSettingsData {
  likelihoodLevels: RiskScoringLevel[];
  impactLevels: RiskScoringLevel[];
  impactCategories: ImpactCategory[];
  matrix: MatrixEntry[];
}

export interface ChangeDetectionResult {
  hasChanges: boolean;
}

const areLevelsEqual = (
  a: RiskScoringLevel[],
  b: RiskScoringLevel[]
): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((level, i) => {
    const other = b[i];

    return (
      level.rating === other.rating &&
      level.title === other.title &&
      level.description === other.description &&
      level.color === other.color
    );
  });
};

const areCategoriesEqual = (
  a: ImpactCategory[],
  b: ImpactCategory[]
): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((category, i) => {
    const other = b[i];

    return category.name === other.name && category.color === other.color;
  });
};

const areMatrixEntriesEqual = (a: MatrixEntry[], b: MatrixEntry[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((entry, i) => {
    const other = b[i];

    if (
      entry.title !== other.title ||
      entry.value !== other.value ||
      entry.color !== other.color
    ) {
      return false;
    }

    if (entry.likelihoodImpact.length !== other.likelihoodImpact.length) {
      return false;
    }

    return entry.likelihoodImpact.every((li, j) => {
      const otherLi = other.likelihoodImpact[j];

      return (
        li.likelihood === otherLi.likelihood && li.impact === otherLi.impact
      );
    });
  });
};

export const detectChanges = (
  initial: ScoringSettingsData,
  current: ScoringSettingsData
): ChangeDetectionResult => {
  const hasChanges =
    !areLevelsEqual(initial.likelihoodLevels, current.likelihoodLevels) ||
    !areLevelsEqual(initial.impactLevels, current.impactLevels) ||
    !areCategoriesEqual(initial.impactCategories, current.impactCategories) ||
    !areMatrixEntriesEqual(initial.matrix, current.matrix);

  return { hasChanges };
};
